import { Translations } from '@domain/admission';
import { TFunction } from 'i18next';

export interface Channel {
  id: string;
  maxQuantity: string | number | null;
}

export interface OfferTeam {
  offerId: string;
  teamId: string;
  teamName: string;
  quantity: number | null;
}

export interface SaveOfferTeamMaxQuantityRequest {
  offerId: string;
  teamId: string;
  maxQuantity: number | null;
}

interface FormField {
  /** Present for fields that already exist on the offer — lets the BE reconcile by id (B2B-1699). */
  id?: string;
  name: string;
  dataType: string;
  isRequired: boolean;
  /** `false` deactivates the field (soft-delete); absent ≡ active. Part of the additive contract. */
  isActive?: boolean;
  /** Hard-deletes the field by id; other attributes travel along for consistency but BE ignores them. */
  deleted?: boolean;
  max?: number;
  min?: number;
}

export interface CreateAdmissionRequest {
  eventId?: string;
  price?: {
    amount?: number;
    currency: string;
    onlinePrice?: number;
    offlinePrice?: number;
  };
  isDraft?: boolean;
  quantity?: number | null;
  salesTime?: {
    openingTime?: number | null;
    closingTime?: number;
  };
  offerReleasedBefore?: string | null;
  // The admission form is the only producer of these fields and always
  // submits a `Translations`-shaped block (with empty locales coerced to
  // `null` by `clearEmptyValues`). Typing them narrowly here lets the
  // orchestrator's `createVariables` flow into `<AdmissionGhostCard>`
  // without casts — see B2B-1572. Note: the addon side
  // (`CreateAddonRequest`) still uses `object` and casts at the call
  // site; we go narrower here because we control both the producer and
  // every consumer in this namespace.
  name?: Translations;
  description?: Translations;
  addOns?: string[];
  salesStatus?: {
    isSoldOut: boolean;
    isSalesClosed: boolean;
  };
  itemsPerTransaction?: {
    min: number;
    max: number;
  };
  onlinePaymentPercentage?: number;
  guestCapacity?: number;
  settings?: {
    isHidden?: boolean;
    isPrivate?: boolean;
    /**
     * Offer distribution channel (B2B-1676), supersedes the deprecated `isPrivate`.
     * `offline` ≡ old `isPrivate: true`, `online` ≡ `isPrivate: false`,
     * `online_and_offline` is the new sold-online-and-at-the-door state.
     */
    distribution?: 'online' | 'offline' | 'online_and_offline';
    isForAllChannels?: boolean;
    isNominative?: boolean;
    italyRegulation?: {
      dirittoPrevendita: number | null;
      isSiaeExcluded: boolean;
      // Widened for B2B-1697 so the new admission form persists the SIAE / Carta Cultura
      // fields on the Ticket Create/Edit endpoints. BE already accepts them, mirroring
      // CreatePassRequest's italyRegulation. Read side was already typed in src/domain/admission.ts
      // (Settings.italyRegulation); only this write side was missing — discovered in B2B-1694.
      isSiaeApproved?: boolean;
      allows18App?: boolean;
      abbonamentoId?: string | null;
    } | null;
    channelDefaultMaxQuantity?: number | null;
  };
  channels?: Channel[];
  bookingFormFields?: FormField[];
  ticketHolderFormFields?: FormField[];
  accessCode?: string | null;
  floorplanId?: string;
  drinksIncluded?: number;
  checkInTime?: {
    validFrom?: number | null;
    validUntil?: number | null;
  };
  color?: string | null;
  hasMandatoryAddOns?: boolean;
  addOnChoiceMode?: string;
  vatRate?: number | null;
  ticketsLeftThreshold?: number | null;
  offerPresetId?: string;
}

export interface Action {
  name: string;
  action: () => void;
}

export const ADMISSION_CARD_TYPE = {
  pass: 'pass',
  offer: 'offer',
} as const;

export interface AdmissionCardActionsConfig {
  // Card type
  type: (typeof ADMISSION_CARD_TYPE)[keyof typeof ADMISSION_CARD_TYPE];

  // Common conditions
  isPreset: boolean;
  isPastEvent?: boolean;
  isSharePaymentLinkActive: boolean;
  isOpenForAll?: boolean;
  deleteIsUnassign?: boolean;
  isGroupCard?: boolean;
  isEnabledSection?: boolean;

  // Pass-specific conditions
  isToggleActive?: boolean;
  isExportQrCodesActive?: boolean;

  // Available callbacks
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDeletePass?: (id: string) => void;
  onDuplicate?: () => void;
  onUnassign?: (id: string) => void;
  onClickViewHolders?: () => void;
  onClickAddHolder?: () => void;
  onClickViewEvents?: () => void;
  onClickExportQrCodes?: () => void;

  // Action handlers (already wrapped in useCallback from parent)
  handleEdit: () => void;
  handleDelete: () => void;
  handleDeletePass: () => void;
  handleDuplicate: () => void;
  handleUnassign: () => void;
  handleViewHolders: () => void;
  handleAddHolder: () => void;
  handleViewEvents: () => void;
  handleExportQrCodes: () => void;
  handleShareLink: () => void;
  handleAddGroup: () => void;
  handleRemoveFromGroup: () => void;
  handleMoveToAnotherGroup: () => void;
  // i18n
  t: TFunction;
}

export interface ActionBuilderContext {
  actions: Action[];
  config: AdmissionCardActionsConfig;
  t: TFunction;
}
